import {Directive, ElementRef, HostListener} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

const FLOAT_NUMBER_PRECISION = 2;
const INT_PART_LIMIT = 13;
const NUMBER_MAX_LENGTH = INT_PART_LIMIT + 1 + FLOAT_NUMBER_PRECISION;

@Directive({
    selector: 'input[commonsFloatNumberInput]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FloatNumberInputDirective,
            multi: true,
        },
    ],
    standalone: false
})
export class FloatNumberInputDirective implements ControlValueAccessor {
    private lastValidValue: string = '';

    private onChange: (value: number | null) => void;
    private onTouched: () => void;

    constructor(private elementRef: ElementRef) {
    }

    @HostListener('input', ['$event.target.value'])
    onInput(value: string): void {
        const cursorPosition = this.getCursorPosition();
        const sanitizedValue = this.sanitizeInput(value);
        this.updateValue(sanitizedValue);
        this.setCursorPosition(cursorPosition, sanitizedValue);
    }

    @HostListener('blur')
    onBlur(): void {
        this.updateValue(this.removeExtraZerosInFractionalPart(this.lastValidValue), true);
    }

    writeValue(value: number | null): void {
        this.lastValidValue = this.removeExtraZerosInFractionalPart(value?.toString() || '');
        this.elementRef.nativeElement.value = this.lastValidValue;
    }

    registerOnChange(fn: (value: number | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.elementRef.nativeElement.disabled = isDisabled;
    }

    private sanitizeInput(value: string): string {
        let sanitized = value.replace(/[^0-9.]/g, '');
        if (sanitized === '') {
            return sanitized;
        }
        if (sanitized.startsWith('.')) {
            sanitized = '0' + sanitized;
        }
        sanitized = this.removeExtraDots(sanitized);
        sanitized = this.removeLeadingZeros(sanitized);
        sanitized = this.limitFractionalPart(sanitized);
        return this.limitLength(sanitized);
    }

    private removeLeadingZeros(value: string): string {
        const parts = value.split('.');
        const integerPart = parseInt(parts[0]).toString();
        const fractionalPart = parts[1];
        return parts.length > 1 ? `${integerPart}.${fractionalPart}` : integerPart;
    }

    private removeExtraZerosInFractionalPart(value: string): string {
        const [integer, fraction] = value.split('.');
        const fractionWithoutExtraZeros = fraction?.replace(/0+$/, '');
        return fractionWithoutExtraZeros ? `${integer}.${fractionWithoutExtraZeros}` : integer;
    }

    private removeExtraDots(value: string): string {
        const dotIndex = value.indexOf('.');
        return dotIndex !== -1
            ? value.substring(0, dotIndex + 1) + value.substring(dotIndex + 1).replace(/\./g, '')
            : value;
    }

    private limitFractionalPart(value: string): string {
        const [integer, fraction] = value.split('.');
        return fraction?.length > FLOAT_NUMBER_PRECISION
            ? `${integer}.${fraction.slice(0, FLOAT_NUMBER_PRECISION)}`
            : value;
    }

    private limitLength(value: string): string {
        return value.slice(0, NUMBER_MAX_LENGTH);
    }

    private updateValue(value: string, triggerTouched = false): void {
        this.lastValidValue = value;
        this.elementRef.nativeElement.value = value;
        this.onChange && this.onChange(value !== '' ? parseFloat(value) : null);
        if (triggerTouched && this.onTouched) {
            this.onTouched();
        }
    }

    private getCursorPosition(): number {
        return this.elementRef.nativeElement.selectionStart || 0;
    }

    private setCursorPosition(cursorPosition: number, value: string): void {
        const inputElement = this.elementRef.nativeElement;
        const newPosition = Math.min(cursorPosition, value.length);
        inputElement.setSelectionRange(newPosition, newPosition);
    }
}
